import { KINDS, SYNC_TABLES, splitPatch, setFlat } from '../sync/schema.js';
import { nextHlc } from './meta.js';

export function allTables(db) {
  return [...SYNC_TABLES.map((t) => db[t]), db.outbox, db.meta];
}

/** Run `fn` in a read-write transaction covering every synced table + outbox + meta. */
export function writeTx(db, fn) {
  return db.transaction('rw', allTables(db), fn);
}

/**
 * The only way app code changes replicated data. The row update, its HLC stamps
 * and the outbox entry commit atomically, so a change can never be lost between
 * "saved locally" and "queued for sync".
 */
export async function applyLocal(db, kind, id, patch, { now } = {}) {
  const { synced, local } = splitPatch(kind, patch);
  return applyLocalFlat(db, kind, id, synced, { now, local });
}

/** Like applyLocal, but takes already-flattened replicated keys ("fields.front"). */
export async function applyLocalFlat(db, kind, id, synced, { now, local = {} } = {}) {
  return writeTx(db, async () => {
    const table = db[KINDS[kind].table];
    const keys = Object.keys(synced);
    const existing = await table.get(id);
    const row = existing ? { ...existing, _hlc: { ...(existing._hlc ?? {}) } } : { id, _hlc: {} };
    Object.assign(row, local);
    if (keys.length) {
      const hlc = await nextHlc(db, now);
      for (const k of keys) {
        setFlat(row, k, synced[k] === undefined ? null : synced[k]);
        row._hlc[k] = hlc;
      }
      await db.outbox.add({ kind, id, keys, at: Date.now() });
    }
    await table.put(row);
    return row;
  });
}

/** Apply many mutations in a single transaction. ops: [{ kind, id, patch }] */
export async function applyLocalMany(db, ops) {
  return writeTx(db, async () => {
    const rows = [];
    for (const op of ops) rows.push(await applyLocal(db, op.kind, op.id, op.patch));
    return rows;
  });
}

/** Queue every key of every local row for upload (used for linking and self-heal). */
export async function enqueueAll(db) {
  return writeTx(db, async () => {
    const entries = [];
    for (const [kind, def] of Object.entries(KINDS)) {
      const rows = await db[def.table].toArray();
      for (const row of rows) {
        const keys = Object.keys(row._hlc ?? {});
        if (keys.length) entries.push({ kind, id: row.id, keys, at: Date.now() });
      }
    }
    await db.outbox.bulkAdd(entries);
    return entries.length;
  });
}
